import { reloadApp } from "../../../engine/storage/index.js";
import * as StorageHealth from "../../../engine/sync/storage-health.js";
import { escapeHtml } from "../../../engine/core/validation.js";
import { t } from "../../../shared/i18n/index.js";

let modalEl = null;
let statusEl = null;
let recoverBtn = null;
let logoutBtn = null;
let beforeUnloadHandler = null;
let initialized = false;
let allowNavigation = false;

export function initStorageBrokenModal({ forceCompaction, logout } = {}) {
  if (initialized) return;
  initialized = true;

  StorageHealth.onChange(({ state, reason }) => {
    if (state === StorageHealth.StorageHealthState.BROKEN) {
      showStorageBrokenModal({ reason, forceCompaction, logout });
    }
  });

  if (StorageHealth.getState() === StorageHealth.StorageHealthState.BROKEN) {
    showStorageBrokenModal({
      reason: StorageHealth.getReason(),
      forceCompaction,
      logout,
    });
  }
}

function showStorageBrokenModal({ reason, forceCompaction, logout }) {
  if (modalEl) {
    updateReason(reason);
    return;
  }

  ensureStyles();

  modalEl = document.createElement("div");
  modalEl.className = "storage-broken-modal-overlay";
  modalEl.setAttribute("role", "alertdialog");
  modalEl.setAttribute("aria-modal", "true");
  modalEl.innerHTML = `
    <div class="storage-broken-modal">
      <div class="storage-broken-eyebrow">${escapeHtml(t("app.storageBroken.eyebrow"))}</div>
      <h2>${escapeHtml(t("app.storageBroken.title"))}</h2>
      <p>
        ${escapeHtml(t("app.storageBroken.intro"))}
      </p>
      <div class="storage-broken-reason">
        <span>${escapeHtml(t("app.storageBroken.reason"))}</span>
        <strong id="storage-broken-reason"></strong>
      </div>
      <div class="storage-broken-status" id="storage-broken-status">
        ${escapeHtml(t("app.storageBroken.hint"))}
      </div>
      <div class="storage-broken-actions">
        <button type="button" class="storage-broken-recover" id="storage-broken-recover">
          ${escapeHtml(t("app.storageBroken.recover"))}
        </button>
        <button type="button" class="storage-broken-logout" id="storage-broken-logout">
          ${escapeHtml(t("common.logout"))}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modalEl);
  statusEl = modalEl.querySelector("#storage-broken-status");
  recoverBtn = modalEl.querySelector("#storage-broken-recover");
  logoutBtn = modalEl.querySelector("#storage-broken-logout");

  updateReason(reason);
  installBeforeUnloadGuard();

  recoverBtn.addEventListener("click", async () => {
    recoverBtn.disabled = true;
    logoutBtn.disabled = true;
    statusEl.textContent = t("app.storageBroken.sendingSnapshot");

    try {
      const ok = await forceCompaction?.("emergency");
      if (!ok) {
        throw new Error(t("app.storageBroken.noAck"));
      }
      statusEl.textContent = t("app.storageBroken.snapshotSaved");
      const reloading = await reloadApp(() => {
        StorageHealth.markRecovered();
        allowNavigation = true;
      });
      if (!reloading) throw new Error(t("app.storageBroken.localNotSaved"));
    } catch (err) {
      console.error("[StorageBrokenModal] Recovery failed:", err);
      allowNavigation = false;
      StorageHealth.markBroken(reason);
      statusEl.textContent = t("app.storageBroken.recoveryFailed", { error: err?.message || String(err) });
      recoverBtn.disabled = false;
      logoutBtn.disabled = false;
    }
  });

  logoutBtn.addEventListener("click", async () => {
    recoverBtn.disabled = true;
    logoutBtn.disabled = true;
    statusEl.textContent = t("app.storageBroken.loggingOut");
    try {
      await logout?.();
      statusEl.textContent = t("app.storageBroken.loggedOut");
      const reloading = await reloadApp(() => { allowNavigation = true; });
      if (!reloading) throw new Error(t("app.storageBroken.localNotSavedRetry"));
    } catch (err) {
      console.error("[StorageBrokenModal] Logout failed:", err);
      allowNavigation = false;
      StorageHealth.markBroken(reason);
      statusEl.textContent = t("app.storageBroken.logoutFailed", { error: err?.message || String(err) });
      recoverBtn.disabled = false;
      logoutBtn.disabled = false;
    }
  });
}

function updateReason(reason) {
  const reasonEl = modalEl?.querySelector("#storage-broken-reason");
  if (reasonEl) reasonEl.textContent = humanizeReason(reason);
}

function humanizeReason(reason) {
  if (!reason) return t("app.storageBroken.why.unavailable");
  const exact = {
    indexeddb_quota: "indexeddbQuota",
    indexeddb_unavailable: "indexeddbUnavailable",
    indexeddb_load_failed: "indexeddbLoadFailed",
    sync_stalled: "syncStalled",
  };
  if (exact[reason]) return t(`app.storageBroken.why.${exact[reason]}`);
  // "<code>: <detail>" reasons; the detail is technical and stays as is.
  const withDetail = {
    compaction_failed: "compactionFailed",
    server_rejected: "serverRejected",
    sync_send_failed: "sendFailed",
    delta_ack_timeout: "ackTimeout",
    delta_ack_lost: "ackLost",
    user_delta_ack_timeout: "vaultAckTimeout",
    user_delta_ack_lost: "vaultAckLost",
    user_server_rejected: "vaultRejected",
  };
  for (const [code, key] of Object.entries(withDetail)) {
    if (reason.startsWith(`${code}:`)) return t(`app.storageBroken.why.${key}`, { detail: reason.slice(code.length + 1).trim() });
  }
  return reason;
}

function installBeforeUnloadGuard() {
  if (beforeUnloadHandler) return;
  beforeUnloadHandler = (event) => {
    if (allowNavigation) return;
    if (StorageHealth.getState() !== StorageHealth.StorageHealthState.BROKEN) return;
    event.preventDefault();
    event.returnValue = t("app.storageBroken.leaveWarning");
    return event.returnValue;
  };
  window.addEventListener("beforeunload", beforeUnloadHandler);
}

function ensureStyles() {
  if (document.getElementById("storage-broken-modal-styles")) return;
  const style = document.createElement("style");
  style.id = "storage-broken-modal-styles";
  style.textContent = `
    .storage-broken-modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 20000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: rgba(5, 10, 20, 0.82);
      backdrop-filter: blur(8px);
    }
    .storage-broken-modal {
      width: min(560px, 100%);
      padding: 28px;
      border-radius: 18px;
      background: linear-gradient(145deg, #171d2b, #0b1020);
      border: 1px solid rgba(255, 255, 255, 0.14);
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.45);
      color: #f8fafc;
    }
    .storage-broken-eyebrow {
      margin-bottom: 10px;
      color: #fbbf24;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .storage-broken-modal h2 {
      margin: 0 0 12px;
      font-size: 26px;
      line-height: 1.15;
    }
    .storage-broken-modal p {
      margin: 0 0 18px;
      color: #cbd5e1;
      line-height: 1.5;
    }
    .storage-broken-reason {
      margin: 18px 0;
      padding: 14px;
      border-radius: 12px;
      background: rgba(248, 113, 113, 0.12);
      border: 1px solid rgba(248, 113, 113, 0.32);
    }
    .storage-broken-reason span {
      display: block;
      margin-bottom: 4px;
      color: #fca5a5;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .storage-broken-reason strong {
      color: #fff7ed;
      font-weight: 600;
    }
    .storage-broken-status {
      min-height: 22px;
      margin: 16px 0 22px;
      color: #dbeafe;
      line-height: 1.45;
    }
    .storage-broken-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    .storage-broken-actions button {
      border: 0;
      border-radius: 999px;
      padding: 11px 18px;
      font-weight: 700;
      cursor: pointer;
    }
    .storage-broken-actions button:disabled {
      opacity: 0.55;
      cursor: wait;
    }
    .storage-broken-recover {
      background: #fbbf24;
      color: #111827;
    }
    .storage-broken-logout {
      background: rgba(255, 255, 255, 0.12);
      color: #f8fafc;
    }
  `;
  document.head.appendChild(style);
}
